import { useEffect, useRef, useState, type ReactNode } from "react";
import { Twitter, Facebook, Instagram, Link2, Check, MessageCircle, Share2, ChevronDown, Pin as PinIcon, Send, Linkedin, Mail } from "lucide-react";
import { toast } from "sonner";
import { trackConversion } from "@/hooks/use-page-view";
import { useLocale } from "@/hooks/use-locale";
import { curatedUiTranslation } from "@/lib/curated-ui-translations";

type Props = { url: string; title: string; compact?: boolean };

// Reddit's snoo isn't in lucide; use inline SVG.
function RedditIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M14.5 15.41c.08.09.08.28 0 .39-.43.42-1.26.55-1.97.55s-1.55-.13-1.97-.55a.31.31 0 010-.39c.11-.1.28-.1.39 0 .27.28.85.38 1.58.38s1.31-.1 1.58-.38c.11-.1.28-.1.39 0zM9.5 13.5c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1zm6 0c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1zM22 12c0-1.1-.9-2-2-2-.51 0-.97.19-1.32.5-1.41-.91-3.21-1.49-5.18-1.56l1.06-3.36 2.79.65a1.5 1.5 0 102.95-.39 1.5 1.5 0 00-2.66.95l-3.13-.73-1.25 3.97c-1.94.08-3.71.66-5.1 1.55C7.81 10.69 7.42 10.5 7 10.5c-1.1 0-2 .9-2 2 0 .73.39 1.36.97 1.71-.03.21-.05.42-.05.64 0 2.61 3.13 4.73 7 4.73s7-2.12 7-4.73c0-.22-.02-.43-.05-.64.58-.35.97-.98.97-1.71z" />
    </svg>
  );
}

/**
 * Single-button Share menu. Replaces the previous row of 6 buttons for a
 * cleaner, less overwhelming UX site-wide. Uses native share when available
 * on mobile, otherwise opens a small popover with WhatsApp / X / Reddit /
 * Facebook / Instagram / Copy Link.
 */
export function ShareButtons({ url, title, compact }: Props) {
  const [lang] = useLocale();
  const ui = (source: string) => curatedUiTranslation(lang, source) ?? source;
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const enc = encodeURIComponent;

  // Tag every share with UTMs so Plausible can attribute incoming traffic
  // per-network. Idempotent - leaves any existing query string intact.
  const tagged = (network: string) => {
    try {
      const u = new URL(url, "https://smartasaurus.com");
      u.searchParams.set("utm_source", network.toLowerCase());
      u.searchParams.set("utm_medium", "social");
      u.searchParams.set("utm_campaign", "share");
      return u.toString();
    } catch {
      return url;
    }
  };

  useEffect(() => {
    if (!open) return;
    const onDown = (event: PointerEvent) => {
      const wrapper = wrapRef.current;
      if (!wrapper || !event.composedPath().includes(wrapper)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    const onBlur = () => setOpen(false);
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") setOpen(false);
    };
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("blur", onBlur);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);


  const targets: Array<{ label: string; network: string; href: string; icon: ReactNode }> = [
    { label: "WhatsApp", network: "WhatsApp", href: `https://wa.me/?text=${enc(`${title} ${tagged("whatsapp")}`)}`, icon: <MessageCircle className="h-4 w-4" /> },
    { label: "Facebook", network: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${enc(tagged("facebook"))}`, icon: <Facebook className="h-4 w-4" /> },
    { label: "X", network: "X", href: `https://twitter.com/intent/tweet?url=${enc(tagged("x"))}&text=${enc(title)}`, icon: <Twitter className="h-4 w-4" /> },
    { label: "Pinterest", network: "Pinterest", href: `https://pinterest.com/pin/create/button/?url=${enc(tagged("pinterest"))}&description=${enc(title)}`, icon: <PinIcon className="h-4 w-4" /> },
    { label: "Reddit", network: "Reddit", href: `https://www.reddit.com/submit?url=${enc(tagged("reddit"))}&title=${enc(title)}`, icon: <RedditIcon className="h-4 w-4" /> },
    { label: "Telegram", network: "Telegram", href: `https://t.me/share/url?url=${enc(tagged("telegram"))}&text=${enc(title)}`, icon: <Send className="h-4 w-4" /> },
    { label: "LinkedIn", network: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(tagged("linkedin"))}`, icon: <Linkedin className="h-4 w-4" /> },
    { label: "Email", network: "Email", href: `mailto:?subject=${enc(title)}&body=${enc(`${title}\n\n${tagged("email")}`)}`, icon: <Mail className="h-4 w-4" /> },
  ];

  const onInstagram = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied - paste into Instagram"); }
    catch { toast.error("Couldn't copy link"); }
    window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
    setOpen(false);
  };

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch { toast.error("Couldn't copy link"); }
  };

  const onShareClick = async () => {
    // Prefer native share on mobile.
    const nav = navigator as Navigator & { share?: (d: ShareData) => Promise<void> };
    if (nav.share) {
      try { await nav.share({ title, url }); trackConversion("share_click", "/share/native"); return; }
      catch { /* fall through to popover */ }
    }
    setOpen((v) => !v);
  };

  return (
    <div ref={wrapRef} className={`relative inline-block ${open ? "z-[60]" : ""}`}>
      <button
        type="button"
        onClick={onShareClick}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={ui("Share")}
        title={ui("Share")}
        className={
          compact
            ? "plausible-event-name=Share+Open inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-card text-foreground transition hover:border-primary hover:text-primary"
            : "plausible-event-name=Share+Open inline-flex items-center gap-1.5 rounded-full border border-border bg-card text-xs font-semibold text-foreground transition hover:border-primary hover:text-primary px-3 py-1.5"
        }
      >
        <Share2 className="h-3.5 w-3.5" />
        {!compact && (
          <>
            <span>{ui("Share")}</span>
            <ChevronDown className="h-3 w-3 opacity-60" />
          </>
        )}
      </button>

      {open && (
        <>
          <div
            aria-hidden="true"
            className="fixed inset-0 z-40 bg-background/60 sm:bg-transparent"
            onPointerDown={() => setOpen(false)}
          />
          <div
            role="menu"
            className="fixed inset-x-3 bottom-3 z-50 grid max-h-[70vh] grid-cols-2 gap-1 overflow-y-auto rounded-2xl border border-border bg-popover p-2 shadow-xl sm:absolute sm:inset-x-auto sm:bottom-auto sm:right-0 sm:mt-2 sm:block sm:w-52 sm:rounded-xl sm:p-1"
          >

          {targets.map((t) => (
            <a
              key={t.network}
              href={t.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => { setOpen(false); trackConversion("share_click", `/share/${t.network.toLowerCase()}`); }}
              className={`plausible-event-name=Share+Click plausible-event-network=${t.network} flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-foreground transition hover:bg-muted`}
              role="menuitem"
            >
              {t.icon}
              <span>{t.label}</span>
            </a>
          ))}
          <button
            type="button"
            onClick={() => { trackConversion("share_click", "/share/instagram"); onInstagram(); }}
            className="plausible-event-name=Share+Click plausible-event-network=Instagram flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-foreground transition hover:bg-muted"
            role="menuitem"
          >
            <Instagram className="h-4 w-4" />
            <span>Instagram</span>
          </button>
          <button
            type="button"
            onClick={() => { trackConversion("share_click", "/share/copy"); onCopy(); }}
            className="plausible-event-name=Share+Click plausible-event-network=Copy flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-foreground transition hover:bg-muted"
            role="menuitem"
          >
            {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Link2 className="h-4 w-4" />}
            <span>{ui(copied ? "Copied" : "Copy link")}</span>
          </button>
          </div>
        </>
      )}
    </div>
  );
}
